import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";

export interface BlueWaveTemplateTypes extends PropsWithChildren {
    illustration?: ReactNode;
    productEditionText: string;
    productLogos?: ReactNode[];
    socialProofLogos: ReactNode;
    tagline: string;
    valueProps?: string[];
}

export const BlueWaveTemplate = ({
    illustration,
    productEditionText,
    productLogos,
    socialProofLogos,
    tagline,
    valueProps,
    children
}: BlueWaveTemplateTypes) => {
    return (
        <div css={containerCss}>
            <header css={headerCss}>
                <span css={productLogosCss}>{productLogos}</span>
                <h1 css={productEditionTextCss}>{productEditionText}</h1>
            </header>
            <main css={mainCss}>
                <div css={formColumnCss}>
                    {children}
                    <div css={atlassianLogoCss}>
                        <AtlassianLogo appearance="brand" size="xsmall" />
                    </div>
                </div>
                <div css={contentColumnCss}>
                    {illustration && (
                        <div css={illustrationCss}>{illustration}</div>
                    )}
                    {tagline && <h2 css={taglineCss}>{tagline}</h2>}
                    {socialProofLogos && (
                        <div css={socialProofCss}>{socialProofLogos}</div>
                    )}
                    <div css={valuePropsContainerCss}>
                        {valueProps && (
                            <ul css={valuePropsCss}>
                                {valueProps.map((prop, index) => (
                                    <li key={index}>{prop}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

const atlassianLogoCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: 0
});

const containerCss = css({
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1440' height='237' fill='none'%3E%3Cpath fill='url(%23a)' d='M1440 0H0v208s343.5-90.5 726.5-2 713.5-44.5 713.5-44.5V0Z'/%3E%3Cdefs%3E%3ClinearGradient id='a' x1='1216' x2='316' y1='151' y2='30.5' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%233F82DB'/%3E%3Cstop offset='1' stop-color='%230747A6'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    gap: 48,
    paddingBlockStart: 20,
    [media.above.xs]: {
        padding: 20
    },
    [media.above.md]: {
        backgroundSize: "contain"
    }
});

const contentColumnCss = css({
    flex: 1
});

const formColumnCss = css({
    backgroundColor: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    margin: "0 auto",
    maxWidth: "100%",
    padding: 24,
    width: 414,
    justifyContent: "space-between",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ['button, div[role="presentation"]']: {
        borderRadius: `3px !important`,
        fontSize: 16
    },
    [media.above.xs]: {
        borderRadius: 8,
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0 8px 9pt #091e4226,0 0 1px #091e424f"
    },
    [media.above.md]: {
        padding: 32
    }
});

const headerCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

const illustrationCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: 8,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        height: 224
    }
});

const mainCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 36,
    margin: "0 auto",
    maxWidth: 894,
    width: "100%",
    [media.above.sm]: {
        flexDirection: "row-reverse !important"
    }
});

const productEditionTextCss = css({
    color: wacToken("color.neutral.light0"),
    fontFamily: wacToken("font.charlie.display"),
    fontSize: 20,
    fontWeight: 400,
    margin: 0,
    padding: "0 16px"
});

const productLogosCss = css({
    columnGap: 24,
    display: "flex",
    flexWrap: "wrap",
    rowGap: 4,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["> span, svg"]: {
        display: "block",
        maxWidth: "100%",
        margin: "0 auto"
    }
});

const socialProofCss = css({
    color: wacToken("color.neutral.light500"),
    display: "flex",
    justifyContent: "center"
});

const taglineCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: 20,
    fontWeight: "normal",
    margin: 0,
    textAlign: "center"
});

const valuePropsContainerCss = css({
    display: "grid",
    justifyContent: "center"
});

const valuePropsCss = css({
    fontWeight: 100,
    listStyleImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='13'%3E%3Cpath fill='none' stroke='%230049B0' stroke-linecap='round' stroke-width='1.6' d='m1 6.394 3.982 5.023 7.018-10'/%3E%3C/svg%3E");`,
    margin: 0,
    paddingInlineStart: 16,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    li: {
        margin: "8px 0"
    }
});
